import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { parseCSVFile } from "@/utils/csvUtils";
import {
  ExcelSheetInfo,
  convertExcelSheetToCSV,
  createCSVFileFromContent,
  isExcelFile,
  readExcelFile,
} from "@/utils/excelUtils";
import {
  PlotMetadataValidationResult,
  validatePlotMetadataUpdate,
} from "@/services/firebase";
import { Experiment } from "@/types/experiment";
import { Plot } from "@/types/plot";
import { AlertTriangle, CheckCircle, FileText, Upload } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";

const METADATA_WIPE_WARNING_KEY = "gleanag:hideMetadataWipeWarning";

interface UpdatePlotMetadataSectionProps {
  experiment: Experiment;
  existingPlots: Plot[];
  onUpdate: (parsedData: Record<string, string>[]) => void;
  disabled?: boolean;
  /** Reports whether a metadata file is selected-but-unapplied, so the parent
   *  dialog can treat it as unsaved work. */
  onPendingChange?: (hasPending: boolean) => void;
}

const UpdatePlotMetadataSection: React.FC<UpdatePlotMetadataSectionProps> = ({
  experiment,
  existingPlots,
  onUpdate,
  disabled = false,
  onPendingChange,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<Record<string, string>[] | null>(null);
  const [validationResult, setValidationResult] = useState<PlotMetadataValidationResult | null>(null);

  // Excel processing state
  const [isExcelFileState, setIsExcelFileState] = useState(false);
  const [availableSheets, setAvailableSheets] = useState<ExcelSheetInfo[]>([]);
  const [selectedSheet, setSelectedSheet] = useState<string>("");
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [originalExcelFile, setOriginalExcelFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  // Warning dialog state
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [hideWarning, setHideWarning] = useState(false);

  // Load "don't show again" preference from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(METADATA_WIPE_WARNING_KEY);
    if (stored === "true") {
      setHideWarning(true);
    }
  }, []);

  // A selected-but-unapplied file is unsaved work; surface it so the wrapping
  // edit dialog's unsaved-changes guard fires before discarding it.
  useEffect(() => {
    onPendingChange?.(selectedFile !== null);
  }, [selectedFile, onPendingChange]);

  // Get plot map config from experiment
  const plotMapConfig = {
    plotIDName: experiment.plotMap?.plotIDName || "Plot ID",
    rowIDName: experiment.plotMap?.rowIDName,
    colIDName: experiment.plotMap?.colIDName,
  };

  // Revalidate when existingPlots changes (e.g., after loading completes)
  // This handles the case where user uploads before plots are loaded
  useEffect(() => {
    if (parsedData && existingPlots.length > 0) {
      const result = validatePlotMetadataUpdate(parsedData, existingPlots, plotMapConfig);
      setValidationResult(result);
    }
  }, [existingPlots, parsedData, plotMapConfig.plotIDName, plotMapConfig.rowIDName, plotMapConfig.colIDName]);

  const resetState = () => {
    setSelectedFile(null);
    setParsedData(null);
    setValidationResult(null);
    setFileError(null);
    setIsExcelFileState(false);
    setAvailableSheets([]);
    setSelectedSheet("");
    setOriginalExcelFile(null);
    setIsProcessingFile(false);
  };

  const handleFileClick = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const processCSVFile = async (file: File) => {
    try {
      setIsProcessingFile(true);
      setFileError(null);
      setValidationResult(null);

      const data = await parseCSVFile(file);

      if (!data || data.length === 0) {
        setFileError("The uploaded file contains no data rows.");
        setParsedData(null);
        return;
      }

      setParsedData(data);

      // Validate against existing plots
      const result = validatePlotMetadataUpdate(data, existingPlots, plotMapConfig);
      setValidationResult(result);
    } catch (error) {
      setFileError("Failed to process file. Please ensure it's a valid CSV or Excel file.");
      console.error("File processing error:", error);
    } finally {
      setIsProcessingFile(false);
    }
  };

  const convertExcelSheetToCSVAndProcess = useCallback(
    async (file: File, sheetName: string) => {
      try {
        setIsProcessingFile(true);
        const result = await convertExcelSheetToCSV(file, sheetName);
        if (!result.isValid) {
          setFileError(result.error || "Failed to convert Excel sheet to CSV");
          return;
        }

        const csvFile = createCSVFileFromContent(result.csvContent!, file.name, sheetName);
        setSelectedFile(csvFile);
        await processCSVFile(csvFile);
      } catch (error) {
        setFileError("Failed to convert Excel sheet to CSV. Please try again.");
        console.error("Excel to CSV conversion error:", error);
      } finally {
        setIsProcessingFile(false);
      }
    },
    [existingPlots, plotMapConfig]
  );

  const handleExcelFileUpload = async (file: File) => {
    try {
      setIsProcessingFile(true);
      setFileError(null);
      const result = await readExcelFile(file);
      if (!result.isValid) {
        setFileError(result.error || "Invalid Excel file");
        return;
      }
      setAvailableSheets(result.sheets || []);

      if (result.sheets && result.sheets.length === 1) {
        const sheetName = result.sheets[0].name;
        setSelectedSheet(sheetName);
        await convertExcelSheetToCSVAndProcess(file, sheetName);
      }
    } catch (error) {
      setFileError("Failed to process Excel file. Please try again.");
      console.error("Excel processing error:", error);
    } finally {
      setIsProcessingFile(false);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;

    const file = event.target.files?.[0];
    if (file) {
      resetState();
      setSelectedFile(file);

      const isExcel = isExcelFile(file);
      setIsExcelFileState(isExcel);
      if (isExcel) {
        setOriginalExcelFile(file);
        await handleExcelFileUpload(file);
      } else {
        await processCSVFile(file);
      }
    }

    // Reset the input so the same file can be selected again if needed
    event.target.value = "";
  };

  const handleSheetChange = async (sheetName: string) => {
    setSelectedSheet(sheetName);
    if (originalExcelFile) {
      await convertExcelSheetToCSVAndProcess(originalExcelFile, sheetName);
    }
  };

  const handleUpdateClick = () => {
    if (parsedData && validationResult?.isValid) {
      // If user has opted out of warnings, proceed directly
      if (hideWarning) {
        onUpdate(parsedData);
      } else {
        setShowWarningDialog(true);
      }
    }
  };

  const handleConfirmUpdate = () => {
    // Save preference if checkbox is checked
    if (dontShowAgain) {
      localStorage.setItem(METADATA_WIPE_WARNING_KEY, "true");
      setHideWarning(true);
    }
    setShowWarningDialog(false);
    setDontShowAgain(false);
    if (parsedData) {
      onUpdate(parsedData);
    }
  };

  const handleCancelWarning = () => {
    setShowWarningDialog(false);
    setDontShowAgain(false);
  };

  const handleClearFile = () => {
    resetState();
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Label className="text-base font-medium">Update Plot Metadata</Label>
        <p className="text-sm text-muted-foreground">
          Update metadata for existing plots by uploading a new CSV or Excel file. Plot IDs must match existing plots.
          Row and column positions cannot be changed.
        </p>
      </div>

      {/* File Upload */}
      <div className="space-y-3">
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls,.xlsm"
          onChange={handleFileChange}
          className="hidden"
          disabled={disabled}
        />
        <Button
          type="button"
          variant="outline"
          className={cn(
            "w-full border-dashed border-gray-300 py-2 flex flex-col items-center justify-center gap-2 h-auto",
            disabled && "opacity-50 cursor-not-allowed"
          )}
          onClick={handleFileClick}
          disabled={disabled}
        >
          <Upload className="h-6 w-6 text-gray-400" />
          <div className="text-sm text-gray-600">
            {selectedFile ? (
              isExcelFileState && originalExcelFile ? (
                `Selected: ${originalExcelFile.name}${selectedSheet ? ` (Sheet: ${selectedSheet})` : ""}`
              ) : (
                `Selected: ${selectedFile.name}`
              )
            ) : (
              "Click to upload a CSV or Excel file with updated metadata"
            )}
          </div>
        </Button>

        {selectedFile && !isProcessingFile && (
          <Button type="button" variant="ghost" size="sm" onClick={handleClearFile}>
            Clear file
          </Button>
        )}
      </div>

      {/* Excel Sheet Selection */}
      {isExcelFileState && availableSheets.length > 1 && !disabled && (
        <div className="space-y-3 border rounded-lg p-4 bg-muted/50">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-blue-600" />
            <Label className="text-sm font-medium">Select Worksheet</Label>
          </div>
          <Select value={selectedSheet} onValueChange={handleSheetChange} disabled={disabled}>
            <SelectTrigger>
              <SelectValue placeholder="Select a worksheet" />
            </SelectTrigger>
            <SelectContent>
              {availableSheets.map((sheet) => (
                <SelectItem key={sheet.name} value={sheet.name}>
                  {sheet.name} ({sheet.rowCount} rows, {sheet.columnCount} columns)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Processing State */}
      {isProcessingFile && (
        <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <span className="text-sm text-blue-700">Processing file...</span>
        </div>
      )}

      {/* File Error */}
      {fileError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{fileError}</AlertDescription>
        </Alert>
      )}

      {/* Validation Errors */}
      {validationResult && !validationResult.isValid && validationResult.errors.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-1">
              <div className="font-medium">Validation failed:</div>
              {validationResult.errors.map((error, index) => (
                <div key={index} className="text-sm">
                  {error}
                </div>
              ))}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Validation Success / Preview */}
      {validationResult?.isValid && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            <div className="space-y-2">
              <div className="font-medium">Ready to update metadata</div>
              <div className="text-sm space-y-1">
                <div>
                  <span className="font-medium">{validationResult.matchedPlotIds.length}</span> plots will be updated
                </div>
                {validationResult.metadataColumns.length > 0 && (
                  <div>
                    <span className="font-medium">Metadata columns:</span>{" "}
                    {validationResult.metadataColumns.join(", ")}
                  </div>
                )}
                {existingPlots.length > validationResult.matchedPlotIds.length && (
                  <div className="text-muted-foreground">
                    {existingPlots.length - validationResult.matchedPlotIds.length} plots not in file will remain
                    unchanged
                  </div>
                )}
              </div>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Update Button */}
      {validationResult?.isValid && (
        <Button
          type="button"
          onClick={handleUpdateClick}
          disabled={disabled}
          className="w-full"
        >
          Update Metadata for {validationResult.matchedPlotIds.length} Plots
        </Button>
      )}

      {/* Metadata Wipe Warning Dialog */}
      <Dialog open={showWarningDialog} onOpenChange={setShowWarningDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Metadata Will Be Replaced
            </DialogTitle>
            <DialogDescription className="text-left space-y-3 pt-2">
              <p>
                This update will <strong>replace all existing metadata</strong> for the selected plots with the values from your file.
              </p>
              <p>
                Any metadata fields not included in your CSV will be <strong>deleted</strong> from those plots. Plots not included in the file will remain unchanged.
              </p>
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center space-x-2 py-2">
            <Checkbox
              id="dontShowAgain"
              checked={dontShowAgain}
              onCheckedChange={(checked) => setDontShowAgain(checked === true)}
            />
            <label
              htmlFor="dontShowAgain"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              Don't show this warning again
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelWarning}>
              Cancel
            </Button>
            <Button onClick={handleConfirmUpdate}>
              Continue with Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UpdatePlotMetadataSection;
